import { CellDoubleClickedEvent, ColDef, PostSortRowsParams } from "ag-grid-community";
import { RaonColDef } from "src/types/grid";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { RemoveRowHandler, postSortHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { useWMS1130_R01Store } from "./Store_WMS1130_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultWMS1130_R01DetailDto, WMS1130_R01DetailDto } from "./Dto_WMS1130_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";

import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useWMS1130_R01Modal } from "./hooks/useWMS1130_R01Modal";
import { useWMS1130_R01CellChange } from "./hooks/WMS1130_R01CellChange";
import { useCodeSearch } from "src/hooks/useCodeSearch";

const COLUMN_STATE_KEY = 'WMS1130_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1130_R01DetailDto> | null>;
	defaultWMS1130_R01DetailRow: WMS1130_R01DetailDto;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const DetailTable_WMS1130_R01 = (props: Props) => {
	const { gridRef, defaultWMS1130_R01DetailRow, clickable, setClickable, handleInsertClick } = props;

	const {
		WMS1130_R01DetailList, setWMS1130_R01DetailList,
		insertWMS1130_R01DetailList, removeWMS1130_R01DetailList, updateWMS1130_R01DetailList,
		selectedWMS1130_R01DetailList, addSelectedWMS1130_R01DetailList, removeSelectedWMS1130_R01DetailList,
		initSelectedWMS1130_R01DetailList,
		deletedWMS1130_R01DetailList, addDeletedWMS1130_R01DetailList,
		originalWMS1130_R01DetailList,
		editWMS1130_R01,
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
	} = useWMS1130_R01Store();
	const { itemStatusList, unitList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useWMS1130_R01Modal();
	const { searchExactItemCode } = useCodeSearch({});

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: WMS1130_R01DetailList,
		defaultGridData: defaultWMS1130_R01DetailRow,
		selectedRow: selectedWMS1130_R01DetailList,
		originalGridData: originalWMS1130_R01DetailList,
		updateRow: (index: number, data: any) => updateWMS1130_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		unit: '단위',
		itemStatus: '상태',
		qty: '입고량',
		cancelQty: '취소량',
		lotNo: 'LOT No',
		remark: '비고',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
	disabledCells: ['itemNm', 'itemSpec', 'unit', 'lotNo'],
	insertedDisabledCells: [''],
	commonDisabledCells: ['itemCd', 'itemStatus','unit', 'qty', ],
	
	pointerCells: [ 'cancelQty', 'remark'],
	insertedPointerCells: ['itemCd', 'itemNm', 'itemSpec', 'itemStatus', 'unit', 'qty', ],
	commonPointerCells: [''],
	
	alignLeftCells: ['']
}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<WMS1130_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: selectedWMS1130_R01DetailList,
			deletedList: deletedWMS1130_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		
		selectedWMS1130_R01DetailList, deletedWMS1130_R01DetailList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	/**
	 * 편집 가능한 컬럼 목록 정의
	 * 사용자가 직접 입력/수정할 수 있는 필드들을 지정
	 */
	const defaultEditableColumns = ['itemCd', 'itemNm', 'itemSpec', 'unit', 'itemStatus', 'qty', 'lotNo', 'remark'];

	const detailTableHandlers = useWMS1130_R01CellChange({
		WMS1130_R01DetailList,
		originalWMS1130_R01DetailList,
		updateWMS1130_R01DetailList,
		gridRef,
		setCodeSearchModalProps,
		customCd: editWMS1130_R01?.customCd,
	});

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<WMS1130_R01DetailDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: RaonColDef<WMS1130_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			editWMS1130_R01?.transportNo === '' ? 
				(params: WMS1130_R01DetailDto) => renderIndexCell(
					params, 
					(innerParams: WMS1130_R01DetailDto) => getRowStyleFlags (
						innerParams,
						{
							deletedList: deletedWMS1130_R01DetailList,
							selectedList: selectedWMS1130_R01DetailList,
							dataList: WMS1130_R01DetailList
						}),
					(rowIndex: number, e: React.MouseEvent) => {
						if (e.ctrlKey) {
							for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
						} else { 
							handleInsertClick(e, rowIndex);
						}
					},
					(rowData) => RemoveRowHandler( 
						gridRef,
						WMS1130_R01DetailList, 
						rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
						setClickable, 
						removeWMS1130_R01DetailList, 
						(valueIndex) => addDeletedWMS1130_R01DetailList([valueIndex.toString()])
					),
				)
			: undefined
		),
		{

			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			editable: ((params: any) => params.data?.inserted === true),
			pasteable: true,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 250,			
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,			
			editable: false,
		},
		{
			headerName: '단위', colId: 'unit', field: 'unit', width: 70,			
			...CommonCodeColumn.options(unitList, false, CommonCodeColumn.change(updateWMS1130_R01DetailList), handleGridDropDownCommitAfter),
		},
		{
			headerName: '상태', colId: 'itemStatus', field: 'itemStatus', width: 60,			
			...CommonCodeColumn.options(itemStatusList, ((params: any) => params.data?.inserted === true), CommonCodeColumn.change(updateWMS1130_R01DetailList), handleGridDropDownCommitAfter),
		},
		{
			headerName: '입고량', colId: 'qty', field: 'qty', width: 80,
			editable: ((params: any) => params.data?.inserted === true),
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 130,
			editable: ((params: any) => params.data?.lotInputOption === 'Y' && params.data?.inserted === true),
		},
		// {
		// 	headerName: '출력 여부', colId: 'printed', field: 'printed', width: 90,
		// 	editable: false,
		// 	cellRenderer: (params: any) => params.value ? 'V' : '',
		// 	cellStyle: (params: any) => ({
		// 		textAlign: 'center',
		// 		fontWeight: params.value ? 'bold' : 'normal',
		// 		color: params.value ? '#10b981' : '#6b7280'
		// 	}),
		// },
		// {
		// 	headerName: '비고', colId: 'remark', field: 'remark', width: 150,			
		// 	editable: true,
		// },
		// ...IndexParameterCell(),
	], [
		deletedWMS1130_R01DetailList, WMS1130_R01DetailList, editWMS1130_R01?.transportNo, handleInsertClick, createCellClassRules,
	]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

const columnDefs: ColDef<WMS1130_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<WMS1130_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);


	const handleRowClick = useRowSelectionHandler({
		dataList: WMS1130_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedWMS1130_R01DetailList,
		deletedList: deletedWMS1130_R01DetailList,
		addSelected: addSelectedWMS1130_R01DetailList,
		removeSelected: removeSelectedWMS1130_R01DetailList,
		clickable, setClickable
	});

	/**
	 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 * @param itemSearchConfig - 품목 검색 설정
	 * @param constraints - 제약 조건 (notTransport 등)
	 * @param keyDownConfig - 키다운 네비게이션 설정
	 */
	const cellEventHandlers = useDetailTableWithItemSearch<WMS1130_R01DetailDto>({
		detailList: WMS1130_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목', // 검색 카테고리
				responseMapping: {
					// 검색 결과 → 테이블 필드 매핑
					itemCd: 'itemCd',      // 품목코드
					itemNm: 'itemNm',      // 품명
					itemSpec: 'itemSpec',  // 규격
					itemUnit: 'unit', // 단위
					locationCd: 'locationCd',
					locationNm: 'locationNm',
				},
				clearFields: {
					itemCd: "",
					itemNm: "",
					itemSpec: "",
					unit: "",
					cost: 0,
					amt: 0,
					locationCd: "",
					locationNm: "",
				},
				searchFunction: searchExactItemCode // 품목 검색 함수
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps, // 모달 설정 함수
		},
		constraints: {
			notEdit: (params) => {
				if(params.data?.inserted === true) return true;
				return false;
			}
		},
		keyDownConfig: {
			qtyField: 'qty',
			enableArrowNavigation: true,
		}
	});

	/**
	 * 행 드래그 상태를 추적하는 ref
	 * 드래그 중일 때는 자동 정렬을 방지하여 드래그 효과를 보여줌
	 */
	const isRowDraggingRef = useRef(false);

	const handlePostSort = useCallback((params: PostSortRowsParams<WMS1130_R01DetailDto>) => {
		// 드래그 중에는 postSortRows에 의한 강제 정렬을 막아 드래그가 눈에 보이도록 함
		if (isRowDraggingRef.current) return;
		postSortHandler(params, WMS1130_R01DetailList, originalWMS1130_R01DetailList, setWMS1130_R01DetailList);
	}, [WMS1130_R01DetailList, originalWMS1130_R01DetailList, setWMS1130_R01DetailList]);

	/**
	 * 하단 고정 행 데이터 생성 (합계 행)
	 * 
	 * 기능:
	 * - 수량과 금액의 총합을 계산
	 * - 하단에 고정된 합계 행으로 표시
	 * - 데이터가 없으면 undefined 반환
	 * 
	 * @returns 합계 행 데이터 또는 undefined
	 */
	const pinnedBottomRowData = useMemo(() => {
		// 삭제된 행은 제외하고 수량 합계 계산
		const totalQty : number = WMS1130_R01DetailList
			.reduce((sum, item) => sum + (deletedWMS1130_R01DetailList.includes(item.itemCd) ? 0 : Number(item.qty) || 0), 0);

		// 하단 고정 행 데이터 반환
		return [{
			itemCd: WMS1130_R01DetailList.length.toLocaleString() + "건",
			qty: totalQty,
		}];
	}, [WMS1130_R01DetailList, deletedWMS1130_R01DetailList]);

	return (
		<GridContextWrapper<WMS1130_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={WMS1130_R01DetailList}
			setPaddedGridData={setWMS1130_R01DetailList}
			initSelected={initSelectedWMS1130_R01DetailList}
			defaultRowData={defaultWMS1130_R01DetailRow}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}	
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<WMS1130_R01DetailDto>
				ref={gridRef}
				rowData={WMS1130_R01DetailList}
				getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}

				disableBuiltinCellSelection={false}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}

				rowClassRules={createRowClassRules<WMS1130_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: selectedWMS1130_R01DetailList,
					deletedList: deletedWMS1130_R01DetailList
				})}

				// 이벤트 핸들러들
				onRowClicked={handleRowClick}                                    
				onCellClicked={cellEventHandlers.handleCellClick}               
				onCellDoubleClicked={cellEventHandlers.handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={detailTableHandlers.handleCellChange}
				onCellEditingStopped={detailTableHandlers.handleCellEditingStopped}
				
				// 하단 고정 행 (합계)
				pinnedBottomRowData={pinnedBottomRowData}
				
				// 행 드래그 설정
                rowDragManaged={true}      // 드래그 관리 활성화
                rowDragEntireRow={true}    // 전체 행 드래그
				// 드래그 이벤트 핸들러들
				onRowDragEnter={() => { 
					// 드래그 시작 시 플래그 설정
					isRowDraggingRef.current = true; 
				}}
                onRowDragEnd={(e: any) => {
					/**
					 * 행 드래그 종료 처리
					 * 
					 * 기능:
					 * - 정렬 해제하여 사용자가 정한 순서를 유지
					 * - 드래그된 순서대로 orderIndex 재설정
					 * - 데이터 목록 업데이트
					 */
                    const api = e.api;
                    
                    // 정렬 해제하여 사용자가 정한 순서를 유지
                    if (api.applyColumnState) {
                        api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                    }
                    
                    // 드래그된 순서대로 데이터 재정렬
                    const reordered: WMS1130_R01DetailDto[] = [];
                    api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                        if (!node.data) return;
                        reordered.push({
                            ...node.data,
                            orderIndex: idx + 1,    // 순서 인덱스 재설정
                            orderIndexSeq: 0,       // 시퀀스 초기화
                        });
                    });
                    
                    // 데이터 목록 업데이트
                    setWMS1130_R01DetailList(reordered);
					isRowDraggingRef.current = false; // 드래그 플래그 해제
                }}
				

				// 정렬 후 처리 (드래그 중에는 자동 정렬 방지)
				onPostSortRows={handlePostSort}
			/>
		</GridContextWrapper>
	)
}
